import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import FloatingBubbles from '../components/FloatingBubbles';
import GlassCard from '../components/GlassCard';
import JellyButton from '../components/JellyButton';
import { ChevronLeft, Type, X, Smile, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PAPER_4R } from '../data/frames';

const filters = [
  { id: 'none', name: 'Original', filter: 'none' },
  { id: 'brightness', name: 'Bright', filter: 'brightness(1.2) contrast(1.1)' },
  { id: 'vintage', name: 'Vintage', filter: 'sepia(0.5) contrast(1.2)' },
  { id: 'candy', name: 'Saturate', filter: 'saturate(1.6) contrast(1.1)' },
  { id: 'cool', name: 'Cool', filter: 'hue-rotate(-15deg) saturate(1.2)' },
  { id: 'bw', name: 'B&W', filter: 'grayscale(1) contrast(1.2)' }
];

const EMOJI_LIST = ['✨', '💖', '🔥', '🌸', '🌈', '📷', '🎉', '🦋', '🧸', '🍦', '🎀', '🎈', '⭐', '🍀', '🍕', '🐱'];

const Filter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const [processedImage, setProcessedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // App States
  const [editMode, setEditMode] = useState('filter'); // 'filter', 'text', 'emoji'
  const [detectedSlots, setDetectedSlots] = useState([]);
  const [loadedImages, setLoadedImages] = useState({ photos: [], overlay: null });

  // Emoji Overlay State
  const [emojis, setEmojis] = useState([]); // [{ id, char, x, y, size }]
  const [draggingEmoji, setDraggingEmoji] = useState(null);

  // Text Overlay State
  const [text, setText] = useState('');
  const [textColor, setTextColor] = useState('#000000');
  const [textSize, setTextSize] = useState(40);
  const [textPos, setTextPos] = useState({ x: 50, y: 50 }); // Percentage
  const [isDraggingText, setIsDraggingText] = useState(false);
  
  const photos = location.state?.photos || [];
  const frame = location.state?.frame;

  useEffect(() => {
    if (!photos || photos.length === 0 || !frame) {
      console.warn('Missing data in Filter.js:', { photos: photos.length, frame: !!frame });
      return;
    }
    loadAllResources();
  }, [photos, frame]);

  useEffect(() => {
    if (loadedImages.overlay) {
      applyFilterAndFrame();
    }
  }, [selectedFilter.id, loadedImages]);

  const loadAllResources = async () => {
    setIsProcessing(true);
    try {
      const loadImage = (src, label) => new Promise((resolve, reject) => {
        const img = new Image();
        if (!src.startsWith('data:') && !src.includes(window.location.host)) {
          img.crossOrigin = 'anonymous';
        }
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load ${label}`));
        img.src = src;
      });

      const [loadedPhotos, overlayImg] = await Promise.all([
        Promise.all(photos.slice(0, frame.slots).map((p, i) => loadImage(p, `Photo ${i+1}`))),
        loadImage(frame.overlayUrl, 'Overlay Image')
      ]);

      setLoadedImages({ photos: loadedPhotos, overlay: overlayImg });

      // Detect slots once
      const detectHoles = (img, expectedSlots) => {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        const tCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
        tCtx.drawImage(img, 0, 0);
        const imgData = tCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const data = imgData.data;
        const holes = [];

        const whiteThreshold = 245;
        const alphaThreshold = 30;

        const step = Math.max(3, Math.floor(Math.min(img.width, img.height) / 250));
        const gridW = Math.floor(img.width / step);
        const gridH = Math.floor(img.height / step);
        const visited = new Uint8Array(gridW * gridH);

        const isHoleCell = (gx, gy) => {
          const x = gx * step;
          const y = gy * step;
          const p = (y * img.width + x) * 4;
          const r = data[p];
          const g = data[p + 1];
          const b = data[p + 2];
          const a = data[p + 3];
          return a < alphaThreshold || (r > whiteThreshold && g > whiteThreshold && b > whiteThreshold);
        };

        for (let gy = 0; gy < gridH; gy++) {
          for (let gx = 0; gx < gridW; gx++) {
            const idx = gy * gridW + gx;
            if (visited[idx]) continue;
            if (!isHoleCell(gx, gy)) continue;

            let minX = gx;
            let maxX = gx;
            let minY = gy;
            let maxY = gy;
            let count = 0;
            let touchesEdge = false;

            const stack = [[gx, gy]];
            visited[idx] = 1;

            while (stack.length) {
              const [cx, cy] = stack.pop();
              count++;

              if (cx === 0 || cy === 0 || cx === gridW - 1 || cy === gridH - 1) {
                touchesEdge = true;
              }

              if (cx < minX) minX = cx;
              if (cx > maxX) maxX = cx;
              if (cy < minY) minY = cy;
              if (cy > maxY) maxY = cy;

              const neighbors = [
                [cx - 1, cy],
                [cx + 1, cy],
                [cx, cy - 1],
                [cx, cy + 1]
              ];
              for (const [nx, ny] of neighbors) {
                if (nx < 0 || ny < 0 || nx >= gridW || ny >= gridH) continue;
                const nIdx = ny * gridW + nx;
                if (visited[nIdx]) continue;
                if (!isHoleCell(nx, ny)) continue;
                visited[nIdx] = 1;
                stack.push([nx, ny]);
              }
            }

            const x = minX * step;
            const y = minY * step;
            const width = (maxX - minX + 1) * step;
            const height = (maxY - minY + 1) * step;

            const minSize = Math.min(img.width, img.height) * 0.08;
            if (!touchesEdge && width >= minSize && height >= minSize && count * step * step >= img.width * img.height * 0.005) {
              holes.push({ x, y, width, height });
            }
          }
        }

        holes.sort((a, b) => (b.width * b.height) - (a.width * a.height));
        const kept = Number.isFinite(expectedSlots) && expectedSlots > 0 ? holes.slice(0, expectedSlots) : holes;

        return kept;
      };

      let slots = detectHoles(overlayImg, frame.slots);
      if (slots.length < frame.slots) {
        const baseW = frame?.designSize?.width || 900;
        const baseH = frame?.designSize?.height || 600;
        const scaleX = overlayImg.width / baseW;
        const scaleY = overlayImg.height / baseH;
        slots = frame.photoLayout.map(s => ({
          x: s.x * scaleX,
          y: s.y * scaleY,
          width: s.width * scaleX,
          height: s.height * scaleY
        }));
      }
      slots.sort((a, b) => (a.y - b.y) || (a.x - b.x));
      setDetectedSlots(slots);

    } catch (err) {
      console.error('Error loading resources:', err);
      toast.error('Gagal memuat sumber daya');
    } finally {
      setIsProcessing(false);
    }
  };

  const applyFilterAndFrame = async () => {
    const canvas = canvasRef.current;
    const { photos: loadedPhotos, overlay: overlayImg } = loadedImages;
    if (!canvas || !overlayImg) return;

    const ctx = canvas.getContext('2d');
    canvas.width = overlayImg.width;
    canvas.height = overlayImg.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = frame.bgColor || '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    loadedPhotos.forEach((img, index) => {
      if (index < detectedSlots.length) {
        const slot = detectedSlots[index];
        
        ctx.save();
        ctx.beginPath();
        ctx.rect(slot.x, slot.y, slot.width, slot.height);
        ctx.clip();

        // Base center position
        ctx.translate(slot.x + slot.width / 2, slot.y + slot.height / 2);
        ctx.scale(-1, 1); // Mirror
        
        const photoScale = Math.max(slot.width / img.width, slot.height / img.height);
        const drawW = img.width * photoScale;
        const drawH = img.height * photoScale;
        
        ctx.filter = selectedFilter.filter;
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
        ctx.restore();
      }
    });

    const finalOverlayCanvas = document.createElement('canvas');
    finalOverlayCanvas.width = canvas.width;
    finalOverlayCanvas.height = canvas.height;
    const fCtx = finalOverlayCanvas.getContext('2d');
    fCtx.drawImage(overlayImg, 0, 0);
    
    const fData = fCtx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = fData.data;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i] > 230 && pixels[i+1] > 230 && pixels[i+2] > 230) {
        pixels[i+3] = 0;
      }
    }
    fCtx.putImageData(fData, 0, 0);
    ctx.drawImage(finalOverlayCanvas, 0, 0);

    setProcessedImage(canvas.toDataURL('image/jpeg', 0.9));
  };

  const addEmoji = (char) => {
    const newEmoji = {
      id: Date.now(),
      char,
      x: 50,
      y: 50,
      size: 60
    };
    setEmojis([...emojis, newEmoji]);
    setEditMode('emoji');
    toast.success('Emoji ditambahkan!');
  };

  const removeEmoji = (id) => {
    setEmojis(emojis.filter(e => e.id !== id));
  };

  const handleMouseDown = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    if (editMode === 'text') {
      setIsDraggingText(true);
    } else if (editMode === 'emoji') {
      // Find top-most emoji near the click
      const clickedEmoji = [...emojis].reverse().find(emo => {
        const dx = Math.abs(emo.x - x);
        const dy = Math.abs(emo.y - y);
        return dx < 10 && dy < 10;
      });

      if (clickedEmoji) {
        setDraggingEmoji(clickedEmoji.id);
      }
    }
  };

  const handleMouseMove = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    if (editMode === 'text' && isDraggingText) {
      setTextPos({ 
        x: Math.max(0, Math.min(100, x)), 
        y: Math.max(0, Math.min(100, y)) 
      });
    } else if (editMode === 'emoji' && draggingEmoji) {
      setEmojis(emojis.map(emo => 
        emo.id === draggingEmoji 
          ? { ...emo, x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) } 
          : emo
      ));
    }
  };

  const handleMouseUp = () => {
    setIsDraggingText(false);
    setDraggingEmoji(null);
  };

  const generateFinalImage = async () => {
    const canvas = document.createElement('canvas');
    const baseImg = await new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = processedImage;
    });

    canvas.width = baseImg.width;
    canvas.height = baseImg.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(baseImg, 0, 0);
    
    // Draw Text
    if (text) {
      ctx.font = `bold ${textSize * (canvas.width / 600)}px 'Fredoka'`;
      ctx.fillStyle = textColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const x = (textPos.x / 100) * canvas.width;
      const y = (textPos.y / 100) * canvas.height;
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 4;
      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
    }

    // Draw Emojis
    emojis.forEach(emo => {
      ctx.font = `${emo.size * (canvas.width / 600)}px serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const x = (emo.x / 100) * canvas.width;
      const y = (emo.y / 100) * canvas.height;
      ctx.fillText(emo.char, x, y);
    });

    return canvas.toDataURL('image/jpeg', 0.9);
  };

  const handleContinue = async () => {
    if (!processedImage) return;
    setIsProcessing(true);
    const finalPhoto = await generateFinalImage();
    setIsProcessing(false);
    navigate('/preview', { state: { photo: finalPhoto, frame, photos } });
  };

  return (
    <div className="candy-gradient-bg min-h-screen flex flex-col py-6 px-4">
      <FloatingBubbles />
      
      <div className="relative z-10 max-w-6xl mx-auto flex-1 flex flex-col w-full">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/40 hover:bg-white/80 transition-all">
            <ChevronLeft className="w-6 h-6 text-pink-600" />
          </button>
          <h2 className="text-2xl font-bold text-gray-800 font-['Fredoka']">Pilih Filter ✨</h2>
          <div className="w-10" />
        </div>

        <div className="grid lg:grid-cols-[1.5fr,1fr] gap-8 flex-1">
          <GlassCard className="flex flex-col items-center justify-center p-4">
            <div 
              ref={containerRef}
              className={`w-full max-w-[600px] aspect-[3/2] rounded-2xl overflow-hidden bg-white/50 shadow-2xl relative select-none ${
                editMode !== 'filter' ? 'cursor-move' : ''
              }`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
            >
              <canvas ref={canvasRef} className="hidden" />
              {isProcessing && !processedImage ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/30 backdrop-blur-sm z-50">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent" />
                </div>
              ) : (
                <>
                  <img src={processedImage} alt="Preview" className="w-full h-full object-contain" />
                  
                  {/* Text Overlay */}
                  {text && (
                    <div
                      className="absolute whitespace-nowrap px-4 py-2 font-['Fredoka'] font-bold select-none pointer-events-none"
                      style={{ 
                        left: `${textPos.x}%`, 
                        top: `${textPos.y}%`,
                        color: textColor,
                        fontSize: `${textSize}px`,
                        transform: 'translate(-50%, -50%)',
                        textShadow: '2px 2px 0px #fff, -2px -2px 0px #fff, 2px -2px 0px #fff, -2px 2px 0px #fff',
                        zIndex: 10
                      }}
                    >
                      {text}
                    </div>
                  )}

                  {/* Emojis Overlay */}
                  {emojis.map(emo => (
                    <div
                      key={emo.id}
                      className={`absolute select-none pointer-events-none ${draggingEmoji === emo.id ? 'scale-125 transition-transform' : ''}`}
                      style={{
                        left: `${emo.x}%`,
                        top: `${emo.y}%`,
                        fontSize: `${emo.size}px`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: 11
                      }}
                    >
                      {emo.char}
                    </div>
                  ))}
                </>
              )}
            </div>
            
            <div className="mt-4 flex gap-2 overflow-x-auto w-full max-w-[600px] pb-2 justify-center">
              <button 
                onClick={() => setEditMode('filter')}
                className={`px-4 py-2 rounded-xl font-bold whitespace-nowrap transition-all ${editMode === 'filter' ? 'bg-pink-500 text-white' : 'bg-white/60 text-gray-600'}`}
              >
                🎨 Filters
              </button>
              <button 
                onClick={() => setEditMode('emoji')}
                className={`px-4 py-2 rounded-xl font-bold whitespace-nowrap transition-all ${editMode === 'emoji' ? 'bg-pink-500 text-white' : 'bg-white/60 text-gray-600'}`}
              >
                🥰 Emoji
              </button>
              <button 
                onClick={() => setEditMode('text')}
                className={`px-4 py-2 rounded-xl font-bold whitespace-nowrap transition-all ${editMode === 'text' ? 'bg-pink-500 text-white' : 'bg-white/60 text-gray-600'}`}
              >
                ✍️ Teks
              </button>
            </div>
          </GlassCard>

          <div className="flex flex-col gap-6">
            {editMode === 'filter' && (
              <GlassCard>
                <h3 className="text-xl font-bold text-gray-800 mb-4 font-['Quicksand']">Filters</h3>
                <div className="grid grid-cols-3 gap-3">
                  {filters.map(f => (
                    <button
                      key={f.id}
                      onClick={() => setSelectedFilter(f)}
                      className={`p-3 rounded-2xl font-bold transition-all text-sm ${
                        selectedFilter.id === f.id ? 'bg-pink-500 text-white shadow-lg' : 'bg-white/60 hover:bg-white/80'
                      }`}
                    >
                      {f.name}
                    </button>
                  ))}
                </div>
              </GlassCard>
            )}

            {editMode === 'emoji' && (
              <GlassCard>
                <h3 className="text-xl font-bold text-gray-800 mb-4 font-['Quicksand'] flex items-center gap-2">
                  <Smile className="w-5 h-5" /> Tambah Emoji
                </h3>
                <div className="grid grid-cols-4 gap-2 mb-6">
                  {EMOJI_LIST.map(char => (
                    <button
                      key={char}
                      onClick={() => addEmoji(char)}
                      className="text-2xl p-2 bg-white/60 hover:bg-white rounded-xl transition-all hover:scale-110"
                    >
                      {char}
                    </button>
                  ))}
                </div>
                
                {emojis.length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-gray-500 uppercase">Daftar Emoji (Geser di gambar)</p>
                    <div className="max-h-[150px] overflow-y-auto space-y-2 pr-2">
                      {emojis.map(emo => (
                        <div key={emo.id} className="flex items-center justify-between bg-white/40 p-2 rounded-xl">
                          <span className="text-xl">{emo.char}</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="30"
                              max="120"
                              value={emo.size}
                              onChange={(e) => setEmojis(emojis.map(en => en.id === emo.id ? { ...en, size: parseInt(e.target.value) } : en))}
                              className="w-24 accent-pink-500"
                            />
                            <button onClick={() => removeEmoji(emo.id)} className="p-1 text-red-400 hover:text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </GlassCard>
            )}

            {editMode === 'text' && (
              <GlassCard>
                <h3 className="text-xl font-bold text-gray-800 mb-4 font-['Quicksand'] flex items-center gap-2">
                  <Type className="w-5 h-5" /> Tambah Teks
                </h3>
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Masukkan teks di sini..."
                      className="w-full px-4 py-3 rounded-xl bg-white/60 border-2 border-pink-100 outline-none focus:border-pink-500 transition-all"
                    />
                    {text && (
                      <button onClick={() => setText('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-pink-500">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Warna</label>
                      <input
                        type="color"
                        value={textColor}
                        onChange={(e) => setTextColor(e.target.value)}
                        className="w-full h-10 p-1 rounded-xl bg-white/60 border-2 border-pink-100 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 mb-1 uppercase">Ukuran</label>
                      <input
                        type="range"
                        min="20"
                        max="80"
                        value={textSize}
                        onChange={(e) => setTextSize(parseInt(e.target.value))}
                        className="w-full h-10 accent-pink-500"
                      />
                    </div>
                  </div>
                </div>
              </GlassCard>
            )}

            <JellyButton onClick={handleContinue} disabled={!processedImage || isProcessing} className="w-full text-base sm:text-xl py-4 sm:py-6">
              Lanjut ke Preview →
            </JellyButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filter;
